/**
 * Refinery advisor — "vender ore o reprocesar a minerales?"
 *
 *  - Settings personales (skills, implant, station, tax) en localStorage.
 *  - Auto-save en cada cambio (sin botón Save).
 *  - Yield efectivo mostrado en vivo, calculado en frontend con la misma
 *    fórmula que el backend (apps/api/src/reprocess.ts:computeFinalYield).
 *  - Query keys incluyen signature de settings, así cambian las veredicts
 *    en tiempo real sin refrescar ESI (los precios vienen del KV cache).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { JitaContextBadge } from '../components/JitaContextBadge'
import { CompressionAdvisor } from '../components/CompressionAdvisor'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { useJitaContext, type JitaContextEntry } from '../hooks/useJitaContext'
import {
  type RefinerySettings,
  type ImplantPct,
  type StationBonusPct,
  type RefiningTaxPct,
  DEFAULT_REFINERY_SETTINGS,
  loadRefinerySettings,
  saveRefinerySettings,
  refinerySettingsUpdatedAt,
  computeYieldPct,
} from '../lib/refinerySettings'

type Verdict = 'reprocess' | 'sell_ore' | 'marginal' | 'insufficient_data'

type Advisory = {
  type_id: number
  name: string
  quantity: number
  sell_value_per_unit: number | null
  reprocess_value_per_unit: number | null
  best_action: { verdict: Verdict; reason: string; delta_pct: number | null }
  mineral_breakdown: Array<{
    mineral_id: number
    mineral_name: string
    units_per_unit: number
    unit_price: number | null
  }>
}

const VERDICT_FILTERS: Array<{ key: Verdict | 'all'; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'reprocess', label: 'Reprocesar' },
  { key: 'sell_ore', label: 'Vender ore' },
  { key: 'marginal', label: 'Marginal' },
  { key: 'insufficient_data', label: 'Sin datos' },
]

const SKILL_LEVELS: Array<0 | 1 | 2 | 3 | 4 | 5> = [0, 1, 2, 3, 4, 5]
const IMPLANT_OPTIONS: Array<ImplantPct> = [0, 4, 6, 10]
const STATION_OPTIONS: Array<StationBonusPct> = [0, 1, 3, 6]
const TAX_OPTIONS: Array<RefiningTaxPct> = [0, 2, 5]

/** Signatura corta y estable de los settings — sirve como discriminator del queryKey. */
function settingsKey(s: RefinerySettings): string {
  return `${s.skill_reprocess}-${s.skill_reprocess_efficiency}-${s.implant_pct}-${s.station_bonus_pct}-${s.refining_tax_pct}`
}

/**
 * Tipo del response de `/api/character/refinery-context`. Inline — coincide
 * 1-1 con la salida del backend (apps/api/src/character.ts).
 */
type RefineryContext = {
  skill_reprocess: 0 | 1 | 2 | 3 | 4 | 5
  skill_reprocess_efficiency: 0 | 1 | 2 | 3 | 4 | 5
  reprocessing_implant_pct: 0 | 4 | 6 | 10 | null
  reprocessing_implant_name: string | null
  at_player_structure: boolean
  suggested_station_bonus_pct: 0 | 1 | 3 | 6
  suggested_refining_tax_pct: 0 | 2 | 5
  // Hints de qué se usó para detectar — el user los ve como string para
  // poder auditar ("detectó +3% por faction standing 6.81", etc.). null
  // cuando no se pudo detectar.
  station_bonus_source: string | null
  refining_tax_source: string | null
}

/**
 * Mapea el response del backend al shape de RefinerySettings.
 *
 * El backend es la fuente de verdad para skills + implant (las dos cosas
 * que antes requerían detección manual). Station bonus / refining tax los
 * dejamos editables — para player refineries el bonus depende de los rigs
 * que no podemos leer sin scope corp Director.
 */
function settingsFromContext(ctx: RefineryContext): RefinerySettings {
  return {
    skill_reprocess: ctx.skill_reprocess,
    skill_reprocess_efficiency: ctx.skill_reprocess_efficiency,
    // 0 si el backend no detectó un implant de reprocessing.
    implant_pct: ctx.reprocessing_implant_pct ?? 0,
    station_bonus_pct: ctx.suggested_station_bonus_pct,
    refining_tax_pct: ctx.suggested_refining_tax_pct,
  }
}

export function Refinery() {
  // Carga inicial: si hay algo en localStorage, lo usa. Si NO (es la primera
  // vez o el user apretó "Re-detectar"), caemos a defaults — el useEffect de
  // abajo intenta auto-detectar apenas llegan skills + location.
  const [settings, setSettings] = useState<RefinerySettings>(() => loadRefinerySettings())
  const [verdictFilter, setVerdictFilter] = useState<Verdict | 'all'>('all')
  const [search, setSearch] = useState('')

  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  // Refinery context (single endpoint que devuelve skills + implant auto-detectado
  // + heurística NPC/structure). Comparte el forceRefetchKey con el advice
  // query para que un solo "↻ Refrescar" reinvalide ambos.
  const { data: ctx, dataUpdatedAt: ctxUpdatedAt } = useQuery<RefineryContext>({
    queryKey: ['refinery-context', forceRefetchKey],
    queryFn: () => api.refineryContext(force),
  })
  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['reprocess-advice', forceRefetchKey, settingsKey(settings)],
    queryFn: () => api.reprocessAdvice(settings, force),
  })

  // items declarado ANTES del useMemo que lo usa (TDZ si no).
  const items = data?.items ?? []

  // Jita avg 30d — pill "−8% vs avg 30d" / "+12% vs avg 30d" por ore.
  // Comparte forceRefetchKey así un "↻ Refrescar" re-fetch ambos.
  const orePricesByType = useMemo(() => {
    const m = new Map<number, number | null>()
    for (const it of items) m.set(it.type_id, it.sell_value_per_unit)
    return m
  }, [items])
  const { map: jitaCtxMap } = useJitaContext(
    items.map((it) => it.type_id),
    orePricesByType,
  )

  // Auto-detección: corre apenas llega el context, pero SOLO si el user
  // todavía no tocó los settings (chequeo via _updated_at). Una vez que
  // el user edita algo, esto no vuelve a disparar automáticamente.
  const autoDetectRan = useRef(refinerySettingsUpdatedAt() !== null)
  useEffect(() => {
    if (autoDetectRan.current) return
    if (!ctx) return
    const detected = settingsFromContext(ctx)
    setSettings(detected)
    saveRefinerySettings(detected)
    autoDetectRan.current = true
  }, [ctx])

  const reDetect = () => {
    // Borra localStorage, fuerza re-fetch de skills + advice (vía bump) y
    // deja que el useEffect haga la detección otra vez.
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('eve.refinery.settings.v1')
    }
    autoDetectRan.current = false
    setSettings({ ...DEFAULT_REFINERY_SETTINGS })
    bump()
  }

  const update = (patch: Partial<RefinerySettings>) => {
    const next = { ...settings, ...patch }
    setSettings(next)
    saveRefinerySettings(next)
  }

  const summary = data?.summary
  const yieldPct = computeYieldPct(settings)
  const detectedFromCharacter = refinerySettingsUpdatedAt() !== null && ctx !== undefined

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((it) => {
      if (verdictFilter !== 'all' && it.best_action.verdict !== verdictFilter) return false
      if (q && !it.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [items, verdictFilter, search])

  return (
    <div className="space-y-6">
      {/* ================================================================ */}
      {/* Header                                                            */}
      {/* ================================================================ */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Refinery</h1>
          <p className="text-eve-muted text-sm">
            Para cada ore en tu hangar: ¿te conviene venderlo como está o
            reprocesarlo a minerales? Tu setup personal — se guarda en este
            navegador.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* ================================================================ */}
      {/* Settings                                                          */}
      {/* ================================================================ */}
      <div className="card">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-baseline gap-3">
            <span className="stat-label">Tu setup</span>
            {detectedFromCharacter && ctx && (
              <span className="pill pill-gold text-[9px]">auto-detectado</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-eve-muted">
              <span className="text-eve-text font-mono">{yieldPct.toFixed(1)}%</span>{' '}
              yield efectivo · tax <span className="text-eve-text font-mono">{settings.refining_tax_pct}%</span>
            </div>
            <button
              onClick={reDetect}
              title="Borra los settings locales y vuelve a leer de tu personaje (skills + location)"
              className="text-[11px] uppercase tracking-wider text-eve-muted hover:text-eve-accent border border-eve-border hover:border-eve-accent/40 px-2 py-0.5 rounded transition-colors"
            >
              ↻ Re-detectar
            </button>
          </div>
        </div>

        {!ctx && (
          <div className="text-xs text-eve-muted italic mb-3">
            Leyendo tu personaje para auto-detectar settings (skills + implant + station)…
          </div>
        )}

        {ctx?.reprocessing_implant_name && (
          <div className="text-xs text-eve-muted italic mb-3">
            Implant detectado: <span className="text-eve-text">{ctx.reprocessing_implant_name}</span>
            {' '}(+{ctx.reprocessing_implant_pct ?? 0}%)
          </div>
        )}

        {ctx?.station_bonus_source && (
          <div className="text-xs text-eve-muted italic mb-3">
            Station bonus detectado:{' '}
            <span className="text-eve-text">{ctx.station_bonus_source}</span>
          </div>
        )}

        {ctx?.refining_tax_source && (
          <div className="text-xs text-eve-muted italic mb-3">
            Refining tax detectado:{' '}
            <span className="text-eve-text">{ctx.refining_tax_source}</span>
          </div>
        )}

        <div className="space-y-4">
          {/* Skills */}
          <SettingsRow label="Reprocessing">
            {SKILL_LEVELS.map((lvl) => (
              <PillButton
                key={lvl}
                active={settings.skill_reprocess === lvl}
                onClick={() => update({ skill_reprocess: lvl })}
              >
                L{lvl}
              </PillButton>
            ))}
          </SettingsRow>

          <SettingsRow label="Reprocessing Efficiency">
            {SKILL_LEVELS.map((lvl) => (
              <PillButton
                key={lvl}
                active={settings.skill_reprocess_efficiency === lvl}
                onClick={() => update({ skill_reprocess_efficiency: lvl })}
              >
                L{lvl}
              </PillButton>
            ))}
          </SettingsRow>

          {/* Implant / Station / Tax */}
          <SettingsRow label="Implant" small>
            {IMPLANT_OPTIONS.map((v) => (
              <PillButton
                key={v}
                active={settings.implant_pct === v}
                onClick={() => update({ implant_pct: v })}
              >
                {v === 0 ? 'Ninguno' : `+${v}%`}
              </PillButton>
            ))}
          </SettingsRow>

          <SettingsRow label="Station bonus" small>
            {STATION_OPTIONS.map((v) => (
              <PillButton
                key={v}
                active={settings.station_bonus_pct === v}
                onClick={() => update({ station_bonus_pct: v })}
              >
                {v === 0 ? 'NPC' : `+${v}%`}
              </PillButton>
            ))}
          </SettingsRow>

          <SettingsRow label="Refining tax" small>
            {TAX_OPTIONS.map((v) => (
              <PillButton
                key={v}
                active={settings.refining_tax_pct === v}
                onClick={() => update({ refining_tax_pct: v })}
              >
                {v}%
              </PillButton>
            ))}
          </SettingsRow>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Summary                                                           */}
      {/* ================================================================ */}
      {summary && summary.total_ore_count > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SummaryTile
            label="Si vendés todo el ore"
            value={formatISKShort(summary.sell_total)}
            sub={`${items.length} ores analizados`}
          />
          <SummaryTile
            label="Si reprocesás todo"
            value={formatISKShort(summary.reprocess_total)}
            sub={`${summary.reprocess_wins} reprocesar · ${summary.marginal} marginal · ${summary.sell_wins} vender`}
            accent
          />
          <SummaryTile
            label="Δ (reprocesar – vender)"
            value={formatISKShort(summary.delta_total)}
            sub={
              summary.delta_total > 0
                ? 'Reprocesar gana'
                : summary.delta_total < 0
                  ? 'Vender ore gana'
                  : 'Empate'
            }
            positive={summary.delta_total > 0}
            negative={summary.delta_total < 0}
          />
        </div>
      )}

      {/* ================================================================ */}
      {/* Advisory list                                                     */}
      {/* ================================================================ */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar ore…"
            className="bg-eve-panel border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text placeholder:text-eve-muted focus:border-eve-accent focus:outline-none w-full md:w-64"
          />
          <select
            value={verdictFilter}
            onChange={(e) => setVerdictFilter(e.target.value as Verdict | 'all')}
            className="bg-eve-panel border border-eve-border rounded-md px-2 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none"
          >
            {VERDICT_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
          <span className="text-eve-muted text-xs ml-auto">
            {filtered.length} / {items.length}
          </span>
        </div>

        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : data?.location?.kind === 'in_space' ? (
          <EmptyState msg="Estás en space — volve a dockear para que pueda leer tu hangar." />
        ) : items.length === 0 ? (
          <EmptyState msg="No tenés ore en el hangar dockeado. Solo items no-ore." />
        ) : filtered.length === 0 ? (
          <EmptyState msg="Sin matches para el filtro actual." />
        ) : (
          <div className="space-y-2">
            {filtered.map((it) => (
              <AdvisoryRow
                key={it.type_id}
                item={it}
                yieldPct={yieldPct}
                jitaCtx={jitaCtxMap.get(it.type_id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* Compression advisor — "¿comprimo antes de haul-ear?"             */}
      {/* ================================================================ */}
      <CompressionAdvisor />
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function SettingsRow({
  label,
  small,
  children,
}: {
  label: string
  small?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className={`text-eve-muted ${small ? 'text-xs w-32' : 'text-sm w-44'}`}>{label}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  )
}

function PillButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded text-xs font-mono border transition-colors ${
        active
          ? 'bg-eve-accent text-eve-bg border-eve-accent font-semibold'
          : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text hover:border-eve-accent/40'
      }`}
    >
      {children}
    </button>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  accent,
  positive,
  negative,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
  positive?: boolean
  negative?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : negative
      ? 'text-eve-danger'
      : accent
        ? 'text-eve-accent'
        : 'text-eve-text'
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`stat mt-1 ${valueClass}`}>{value} ISK</div>
      {sub && <div className="text-xs text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function AdvisoryRow({
  item,
  yieldPct,
  jitaCtx,
}: {
  item: Advisory
  yieldPct: number
  jitaCtx?: JitaContextEntry
}) {
  const verdict = item.best_action.verdict
  const sellTotal =
    item.sell_value_per_unit !== null ? item.sell_value_per_unit * item.quantity : null
  const reprocessTotal =
    item.reprocess_value_per_unit !== null
      ? item.reprocess_value_per_unit * item.quantity
      : null
  const deltaPct = item.best_action.delta_pct

  return (
    <details className="border border-eve-border rounded-md bg-eve-bg/40">
      <summary className="cursor-pointer p-3 flex items-center gap-3 hover:bg-eve-panel/50 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-medium">{item.name}</span>
            <span className="text-xs text-eve-muted font-mono">
              × {item.quantity.toLocaleString('en-US')}
            </span>
            <JitaContextBadge
              delta={jitaCtx?.delta_pct ?? null}
              avg={jitaCtx?.avg_30d ?? null}
              current={jitaCtx?.current_price ?? null}
            />
            {deltaPct !== null && (
              <span
                className={`text-[10px] font-mono ${
                  deltaPct >= 5
                    ? 'text-eve-success'
                    : deltaPct <= -5
                      ? 'text-eve-danger'
                      : 'text-eve-muted'
                }`}
              >
                {deltaPct >= 0 ? '+' : ''}
                {deltaPct.toFixed(1)}%
              </span>
            )}
          </div>
          <div className="text-xs text-eve-muted mt-1">{item.best_action.reason}</div>
        </div>
        <div className="flex items-center gap-3">
          <PricePill label="Vender" value={sellTotal} />
          <PricePill label="Reprocesar" value={reprocessTotal} />
          <VerdictBadge verdict={verdict} />
        </div>
      </summary>

      <div className="border-t border-eve-border px-3 py-3 text-xs space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Row label="Venta Jita (por unidad)" value={fmt(item.sell_value_per_unit)} />
          <Row
            label={`Reprocesar (por unidad, yield ${yieldPct.toFixed(1)}%)`}
            value={fmt(item.reprocess_value_per_unit)}
            bold
          />
          <Row label="Δ%" value={deltaPct !== null ? fmtPct(deltaPct) : '—'} />
        </div>

        {item.mineral_breakdown.length > 0 && (
          <div>
            <div className="text-eve-muted text-[10px] uppercase tracking-wider mb-2 font-semibold">
              Breakdown de minerales
            </div>
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-left text-eve-muted border-b border-eve-border">
                  <th className="py-1 font-medium">Mineral</th>
                  <th className="py-1 font-medium text-right">Unidades/unidad ore</th>
                  <th className="py-1 font-medium text-right">Jita buy</th>
                  <th className="py-1 font-medium text-right">Línea ISK</th>
                </tr>
              </thead>
              <tbody>
                {item.mineral_breakdown.map((m) => {
                  const line =
                    m.unit_price !== null
                      ? m.unit_price * m.units_per_unit * yieldPct / 100
                      : null
                  return (
                    <tr key={m.mineral_id} className="border-b border-eve-border/40">
                      <td className="py-1">{m.mineral_name}</td>
                      <td className="py-1 text-right">{m.units_per_unit.toFixed(3)}</td>
                      <td className="py-1 text-right">
                        {m.unit_price !== null ? formatISK(m.unit_price) : '—'}
                      </td>
                      <td className="py-1 text-right text-eve-accent">
                        {line !== null ? formatISK(line) : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </details>
  )
}

function PricePill({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="text-right">
      <div className="text-[10px] text-eve-muted uppercase font-mono">{label}</div>
      <div className="text-sm font-mono">
        {value !== null ? formatISKShort(value) : '—'}
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  bold,
}: {
  label: string
  value: string
  bold?: boolean
}) {
  return (
    <div className={`flex justify-between gap-2 ${bold ? 'font-semibold' : ''}`}>
      <span className="text-eve-muted">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

function VerdictBadge({ verdict }: { verdict: Verdict }) {
  const classes = (() => {
    switch (verdict) {
      case 'reprocess':
        return 'bg-eve-success/10 border-eve-success text-eve-success'
      case 'sell_ore':
        return 'bg-eve-danger/10 border-eve-danger text-eve-danger'
      case 'marginal':
        return 'bg-eve-accent/10 border-eve-accent text-eve-accent'
      case 'insufficient_data':
        return 'bg-eve-muted/10 border-eve-border text-eve-muted'
    }
  })()
  const label = (() => {
    switch (verdict) {
      case 'reprocess':
        return 'Reprocesar'
      case 'sell_ore':
        return 'Vender ore'
      case 'marginal':
        return 'Marginal'
      case 'insufficient_data':
        return 'Sin datos'
    }
  })()
  return (
    <span
      className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${classes}`}
    >
      {label}
    </span>
  )
}

function EmptyState({ msg }: { msg: string }) {
  return <div className="text-eve-muted py-8 text-center italic">{msg}</div>
}

function fmt(n: number | null): string {
  return n !== null ? formatISK(n, 2) : '—'
}

function fmtPct(n: number): string {
  return (n >= 0 ? '+' : '') + n.toFixed(1) + '%'
}
